import { Component, inject, OnInit, ViewChild } from "@angular/core"
import { FormsModule, ReactiveFormsModule } from "@angular/forms"
import { InputTextModule } from "primeng/inputtext"
import { NotifService } from "@services/notif.service"
import { Editor, EditorModule } from "primeng/editor"
import { ButtonModule } from "primeng/button"
import { SubSectionDTO, SubSectionService, SubVersionDTO } from "@services/sub-section.service"
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog"
import { FileUploadModule } from "primeng/fileupload"
import { Editor2Component } from "@components/editor2/editor2.component"
import { REFERENTIAL } from "@pages/admin/referential/referential.service"
import { DropdownRefFirestoreComponent } from "@components/dropdown-ref-firestore/dropdown-ref-firestore.component"
import { DropdownModule } from "primeng/dropdown"
import { DatePipe, JsonPipe } from "@angular/common"
import { ConfirmationService } from "primeng/api"
import { Utils } from "@services/utils"
import { StorageService } from "@services/storage.service"
import { CheckboxModule } from "primeng/checkbox"

@Component({
  selector: "app-admin-subsection",
  standalone: true,
  imports: [
    FormsModule,
    InputTextModule,
    ReactiveFormsModule,
    EditorModule,
    ButtonModule,
    FileUploadModule,
    Editor2Component,
    DropdownRefFirestoreComponent,
    DropdownModule,
    JsonPipe,
    DatePipe,
    CheckboxModule,
  ],
  templateUrl: "./admin-subsection.component.html",
  styleUrl: "./admin-subsection.component.scss",
})
export class AdminSubsectionComponent implements OnInit {
  storageService = inject(StorageService)

  @ViewChild(Editor) editorComponent?: Editor

  public Referential = REFERENTIAL

  subSection: SubSectionDTO | undefined
  currentVersionId = ""
  confirmService = inject(ConfirmationService)
  subSectionService = inject(SubSectionService)
  notifService = inject(NotifService)
  dynamicDialogRef = inject(DynamicDialogRef)
  dialogConfig = inject(DynamicDialogConfig)

  title_internal = ""
  category: any
  program: any

  pendingChanges = false
  pendingChangesVersion = false
  currentVersion?: SubVersionDTO
  savedVersionStr = ""

  getEmptyVersion(): SubVersionDTO {
    return {
      content: "",
      date: new Date(),
      title: "-",
      title_internal: "-",
      id: "",
      hideTitleSection: false,
    }
  }

  async ngOnInit() {
    const id = (this.dialogConfig?.data && this.dialogConfig?.data["id"]) || ""
    const versionId = (this.dialogConfig?.data && this.dialogConfig?.data["versionId"]) || ""
    let subSection: SubSectionDTO | undefined = {
      title_internal: "",
      versions: [],
      category: undefined,
      program: undefined,
      id: "",
    }
    if (id) {
      subSection = await this.subSectionService.get(id)
      subSection.versions = subSection.versions || []
      this.title_internal = subSection.title_internal
      this.category = subSection.category
      this.program = subSection.program
    }

    this.subSection = subSection
    if (subSection.versions.length > 0) {
      this.setCurrentVersion(versionId || subSection.versions[0].id)
    }

    if (this.dynamicDialogRef.close) {
      const close = this.dynamicDialogRef.close
      this.dynamicDialogRef.close = (...args) => {
        if (this.pendingChanges || this.pendingChangesVersion) {
          this.confirmService.confirm({
            message: `Vos modifications en cours seront perdues, voulez-vous continuer ?`,
            header: "Modifications en cours",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Fermer et perdre mes modifications",
            rejectLabel: "Annuler",
            accept: async () => close.apply(this.dynamicDialogRef, [this.subSection]),
            reject: () => {},
          })
        } else {
          close.apply(this.dynamicDialogRef, [this.subSection])
        }
      }
    }
  }

  setCurrentVersion(versionId: string) {
    if (!this.subSection) return
    const currentVersion = this.subSection?.versions?.find((v) => v.id === versionId)
    if (currentVersion) {
      this.currentVersionId = currentVersion.id
      this.currentVersion = {
        ...currentVersion,
        hideTitleSection: currentVersion.hideTitleSection || false,
      }
    }
    this.savedVersionStr = JSON.stringify(this.currentVersion)
    this.checkPendingChanges()
  }

  checkPendingChanges() {
    this.pendingChanges = JSON.stringify(this.currentVersion) !== this.savedVersionStr
    if (!this.currentVersion?.id) this.pendingChanges = true // if currentversion is a new one
  }

  checkPendingChangesVersion() {
    this.pendingChangesVersion =
      this.subSection?.title_internal !== this.title_internal ||
      this.subSection?.category?.id !== this.category?.id ||
      this.subSection?.program?.id !== this.program?.id
  }

  async saveVersion(quit = false) {
    if (!this.subSection?.versions || !this.currentVersion) return

    this.currentVersion.date = new Date()
    const index = this.subSection.versions.findIndex((v: SubVersionDTO) => v.id === this.currentVersionId)
    if (index >= 0 && this.subSection?.versions[index]) {
      //@ts-ignore
      this.subSection.versions[index] = this.currentVersion
    }
    if (!this.currentVersion.id) {
      this.currentVersion.id = this.subSectionService.uuid()
    }

    if (this.subSection?.id) {
      this.subSection = await this.subSectionService.update({
        id: this.subSection.id,
        versions: this.subSection?.versions || [],
      })
      this.notifService.displaySuccess("La version a été enregistrée.")
      this.setCurrentVersion(this.currentVersion.id || "")

      // HACK for updating dropdown versions
      const id = this.currentVersionId
      this.currentVersionId = ""
      await Utils.wait$(10)
      this.currentVersionId = id

      if (quit) this.dynamicDialogRef.close(/*result*/)
    } else {
      throw new Error("saveVersion - id should not be null")
    }
  }

  async newVersion(empty = true) {
    if (!this.subSection) return
    const newVersion = this.getEmptyVersion()
    if (!empty && this.currentVersion) {
      newVersion.title_internal = this.currentVersion.title_internal + "_copy"
      newVersion.title = this.currentVersion.title
      newVersion.content = this.currentVersion.content
      newVersion.backgroundImg = this.currentVersion.backgroundImg
      newVersion.hideTitleSection = this.currentVersion.hideTitleSection
    }
    this.subSection.versions.push(newVersion)
    this.setCurrentVersion(newVersion?.id || "")
  }

  cancelChanges() {
    if (!this.currentVersion?.id && this.subSection?.versions) {
      // if new version not saved yet
      this.subSection.versions = this.subSection.versions.filter((v) => !!v.id)
      this.currentVersion = this.subSection?.versions[0] || this.getEmptyVersion()
    }
    this.setCurrentVersion(this.currentVersion?.id || "")
  }

  versionChange() {
    this.setCurrentVersion(this.currentVersionId)
  }

  async updateMainSubSection() {
    if (this.subSection?.id) {
      this.subSection = await this.subSectionService.update({
        id: this.subSection.id,
        title_internal: this.title_internal,
        category: this.category,
        program: this.program,
      })
      this.notifService.displaySuccess("Les informations de la page ont été enregistrées.")
    } else {
      this.subSection = await this.subSectionService.create({
        title_internal: this.title_internal,
        category: this.category,
        program: this.program,
        versions: [this.getEmptyVersion()],
      })
      this.notifService.displaySuccess("La page a été créée.")
      this.setCurrentVersion(this.subSection.versions[0].id)
    }
    this.checkPendingChangesVersion()
    // this.dynamicDialogRef.close(result)
  }

  async onUpload(data: any) {
    if (!this.currentVersion || !this.subSection) return
    const file = await this.storageService.uploadFile(data.files[0], this.subSection.id || "")
    this.currentVersion.backgroundImg = file?.src || ""
    this.checkPendingChanges()
  }

  deleteBgImage() {
    if (!this.currentVersion || !this.subSection) return
    this.currentVersion.backgroundImg = ""
    this.checkPendingChanges()
  }
}
